import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Card, CardContent, Typography, Box, Link, Chip } from '@mui/material'
import { useDrag } from 'react-dnd'
import { useTranslation } from 'react-i18next'
import { deliveryOrderStatuses } from '../../lib/statuses'
import { formatDeliveryTimeRange } from '../../lib/utils'

const ItemTypes = { ORDER: 'order' }

const resolveCity = (siteBdn, site, address, city) => {
  if (siteBdn?.city) return siteBdn.city
  if (site?.city) return site.city
  if (city) return city
  return null
}

const resolveSite = (siteBdn, site, address, city) => {
  if (siteBdn?.address) return siteBdn.address
  if (site?.address) return [site.address, site.city].filter(Boolean).join(' ')
  if (address) return [address, city].filter(Boolean).join(' ')
  return null
}

const OrderCardFullScreenPlanning = ({ deliveryOrder, fromDriverId, isDragDisabled }) => {
  const { t } = useTranslation('planning_page')
  const [showMore, setShowMore] = useState(false)

  const [{ isDragging }, ref] = useDrag({
    type: ItemTypes.ORDER,
    item: { id: deliveryOrder.id, name: deliveryOrder.name, fromDriverId: fromDriverId },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  const statusEntry = deliveryOrderStatuses().find((s) => s.value === deliveryOrder.status)
  const statusLabel = statusEntry ? t(deliveryOrder.status) : deliveryOrder.status

  const ville = resolveCity(
    deliveryOrder.deliverySiteBdn,
    deliveryOrder.deliverySite,
    deliveryOrder.deliverySiteAddress,
    deliveryOrder.deliverySiteCity
  )

  const depart = resolveSite(
    deliveryOrder.startingSiteBdn,
    deliveryOrder.startingSite,
    deliveryOrder.startingSiteAddress,
    deliveryOrder.startingSiteCity
  )
  const livraison = resolveSite(
    deliveryOrder.deliverySiteBdn,
    deliveryOrder.deliverySite,
    deliveryOrder.deliverySiteAddress,
    deliveryOrder.deliverySiteCity
  )
  const dechargement = resolveSite(
    deliveryOrder.unloadingSiteBdn,
    deliveryOrder.unloadingSite,
    deliveryOrder.unloadingSiteAddress,
    deliveryOrder.unloadingSiteCity
  )

  return (
    <Card
      ref={ref}
      sx={{ width: '100%', opacity: isDragging ? 0.5 : 1, cursor: isDragDisabled ? 'default' : 'grab' }}
    >
      <CardContent sx={{ pb: '12px !important' }}>
        {/* Row 1: client name + status chip */}
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography fontWeight="bold" variant="body1">
            {deliveryOrder.command.clientName}
          </Typography>
          {statusEntry && (
            <Chip
              label={statusLabel}
              size="small"
              sx={{
                backgroundColor: statusEntry.backgroundColor,
                color: statusEntry.color,
                fontWeight: 500,
                ml: 1,
                flexShrink: 0,
              }}
            />
          )}
        </Box>

        {/* Row 2: Horaire */}
        <Box mt={1}>
          <Typography variant="body2">
            <strong>{t('schedule')} :</strong> {formatDeliveryTimeRange(deliveryOrder.deliveryTimeStart, deliveryOrder.deliveryTimeEnd)}
          </Typography>
        </Box>

        {/* Row 3: City */}
        {ville && (
          <Box>
            <Typography variant="body2">
              <strong>{t('city')} :</strong> {ville}
            </Typography>
          </Box>
        )}

        {/* Commande & ODL */}
        <Box mt={1}>
          <Typography variant="body2">
            <strong>{t('orderNumber')} :</strong> {deliveryOrder.command.commandNumber} - {deliveryOrder.orderDeliveryNumber}
          </Typography>
        </Box>

        {/* Voir plus */}
        <Box display="flex" justifyContent="flex-end" mt={1}>
          <Link onClick={() => setShowMore((v) => !v)} sx={{ cursor: 'pointer' }}>
            {showMore ? t('seeLess') : t('seeMore')}
          </Link>
        </Box>

        {showMore && (
          <Box mt={1}>
            <Typography variant="body2">{deliveryOrder.command.contactPhone}</Typography>
            <Typography variant="body2">{deliveryOrder.command.comment}</Typography>
            {(depart || livraison || dechargement) && (
              <Box mt={1}>
                <Typography variant="body2" fontWeight="bold">{t('sites')} :</Typography>
                {depart && <Typography variant="body2">{t('depart')} → {depart}</Typography>}
                {livraison && <Typography variant="body2">{t('delivery')} → {livraison}</Typography>}
                {dechargement && <Typography variant="body2">{t('unloading')} → {dechargement}</Typography>}
              </Box>
            )}
            {deliveryOrder.orderDeliveryMaterials?.nodes?.length > 0 && (
              <Box mt={1}>
                <Typography variant="body2" fontWeight="bold">Produits :</Typography>
                {deliveryOrder.orderDeliveryMaterials.nodes.map((material, index) => (
                  <Typography key={index} variant="body2">
                    {material.material.description}: {material.quantity}{' '}
                    {material.material.measureUnitByBaseMeasureUnitCode.abbr}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

OrderCardFullScreenPlanning.propTypes = {
  deliveryOrder: PropTypes.any.isRequired,
  isDragDisabled: PropTypes.bool,
  fromDriverId: PropTypes.string,
}

export default OrderCardFullScreenPlanning
